import React from 'react'
import moment from 'moment'
import Grid from '@mui/material/Grid'
import Popover from '@mui/material/Popover'
import { IValue } from '@/components/dropDown/DropDown'
import { useTranslation } from 'react-i18next'
import { EDragType } from './calendarTypes'

interface IEventProps {
  start: moment.Moment
  end: moment.Moment
  firstDay: moment.Moment
  onDragClick(dragtype: EDragType): void
  placeId?: number
  placeData?: IValue[]
  comment?: string
  color?: string
  isSelected: boolean
  including?: string[]
  excluding?: string[]
  preview?: boolean
  showPlace?: boolean
}

const Event: React.FC<IEventProps> = ({
  start,
  end,
  firstDay,
  placeId,
  placeData,
  comment,
  color,
  isSelected,
  including,
  excluding,
  preview,
  showPlace,
  onDragClick,
}) => {
  const { t } = useTranslation('default')
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null)
  const isPopoverOpen = Boolean(anchorEl)
  const allowDrag = start.isSameOrBefore(moment(end).subtract(1, 'hours'))
  const [isHovering, setIsHovering] = React.useState(false)
  let placeLabel = ''

  // Check if going over to or from daylight saving time.
  const correctedStart = start.clone()
  const correctedEnd = end.clone()
  if (!firstDay.isDST() && correctedStart.isDST()) {
    correctedStart.subtract(1, 'hours')
    correctedEnd.subtract(1, 'hours')
  } else if (firstDay.isDST() && !correctedStart.isDST()) {
    correctedStart.add(1, 'hours')
    correctedEnd.add(1, 'hours')
  }

  if (placeData) {
    const place = placeData.find((place) => place.id === placeId)
    placeLabel = place?.label
  }
  let backgroundColor = '#FFFFFF'
  if (color) {
    backgroundColor = !preview && isHovering ? `${color}EE` : `${color}DD`
  }

  return (
    <div
      className={!preview ? 'event' : 'event-disabled'}
      onMouseOver={() => !preview && setIsHovering(true)}
      onMouseLeave={() => {
        if (!preview) {
          setIsHovering(false)
        }
        setAnchorEl(null)
      }}
      onMouseEnter={(event) => setAnchorEl(event.currentTarget)}
      style={{
        backgroundColor,
        transition: 'border-width 0.1s ease-in-out',
        borderWidth: isSelected || isHovering ? '2px' : '1px',
      }}
    >
      {!preview && allowDrag && (
        <div
          className="event-top-dragarea"
          onMouseDown={(event) => {
            event.stopPropagation()
            onDragClick(EDragType.Top)
          }}
        />
      )}
      <div
        style={{
          padding: '2px',
          textAlign: 'center',
        }}
      >
        <div style={{ userSelect: 'none' }}>
          {`${correctedStart.format('HH:mm')} - ${correctedEnd.format('HH:mm')}`}
        </div>
        {showPlace && <div style={{ userSelect: 'none' }}>{placeLabel}</div>}
        <br style={{ userSelect: 'none' }} />{' '}
        <br style={{ userSelect: 'none' }} />
        <span
          lang="sv"
          style={{
            overflowWrap: 'anywhere',
            hyphens: 'auto',
          }}
        >
          <Grid
            container
            direction="column"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item xs>
              {comment}
            </Grid>
            {including && including.length ? (
              <Grid item xs>
                {t('calendar.includingUsers')}: {including.join(', ')}
              </Grid>
            ) : (
              ''
            )}
            {excluding && excluding.length ? (
              <Grid item xs>
                {t('calendar.excludingUsers')}: {excluding.join(', ')}
              </Grid>
            ) : (
              ''
            )}
          </Grid>
        </span>
      </div>
      {!preview && allowDrag && (
        <div
          className="event-bottom-dragarea"
          onMouseDown={(event) => {
            event.stopPropagation()
            onDragClick(EDragType.Bottom)
          }}
        />
      )}
      {preview && (
        <Popover
          sx={{ pointerEvents: 'none' }}
          disableScrollLock
          open={isPopoverOpen}
          anchorEl={anchorEl}
          anchorOrigin={{
            vertical: 'center',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          onClose={() => setAnchorEl(null)}
          disableRestoreFocus
        >
          <div
            style={{
              padding: '2px',
              textAlign: 'center',
            }}
          >
            <div>
              {`${correctedStart.format('HH:mm')} - ${correctedEnd.format('HH:mm')}`}
            </div>
            {showPlace && <div>{placeLabel}</div>}
            <span
              lang="sv"
              style={{
                overflowWrap: 'anywhere',
                hyphens: 'auto',
              }}
            >
              <Grid
                container
                direction="column"
                justifyContent="center"
                alignItems="center"
              >
                <Grid item xs>
                  {comment}
                </Grid>
                {including && including.length ? (
                  <Grid item xs>
                    {t('calendar.includingUsers')}: {including.join(', ')}
                  </Grid>
                ) : (
                  ''
                )}
                {excluding && excluding.length ? (
                  <Grid item xs>
                    {t('calendar.excludingUsers')}: {excluding.join(', ')}
                  </Grid>
                ) : (
                  ''
                )}
              </Grid>
            </span>
          </div>
        </Popover>
      )}
    </div>
  )
}

export default Event
